import { Decimal } from 'decimal.js';

/**
 * Final discounted per-month launch-promo price.
 *
 * The promo % is applied to the MONTHLY headline price for BOTH cadences
 * (#4965). On Annual the discount is taken off the monthly headline (27 €
 * Starter / 59 € Pro / 4,90 € Profil+) — NOT the annual-monthly-equivalent
 * (270/12 = 22,50 €), which already bakes in the ~17% annual discount.
 * Discounting that again double-discounts and shows e.g. Starter at 9 €
 * instead of the intended 10,80 € (= 60% off 27). Basing it on the monthly
 * headline matches the actual checkout charge (the coupon applies to the full
 * 12× monthly base) and keeps the struck price, the discounted price and the
 * −% pill mutually consistent on Annual as well as Monthly.
 *
 * Decimal arithmetic avoids float drift; truncated to 2 dp with NO round-up
 * (e.g. 59 × 0.40 = 23.60 stays 23.60; 590 × 0.40 / 12 = 19.666… → 19.66).
 *
 * @param monthlyHeadlinePrice the VAT-adjusted monthly tier price
 * @param promoPct the promo discount percentage (e.g. 40, 60, 80)
 */
export function computePromoMonthlyPrice(
  monthlyHeadlinePrice: number,
  promoPct: number
): number {
  return new Decimal(monthlyHeadlinePrice)
    .mul(new Decimal(100 - promoPct).div(100))
    .toDecimalPlaces(2, Decimal.ROUND_DOWN)
    .toNumber();
}

/**
 * Which price the launch-promo % is taken off — the base passed to
 * {@link computePromoMonthlyPrice}.
 *
 * The promo base is ALWAYS the MONTHLY headline (27 € Starter / 59 € Pro /
 * 4,90 € Profil+), for BOTH cadences (#4965). On Annual, the displayed price
 * is the annual-monthly-equivalent (270/12 = 22,50 € Starter) which already
 * bakes in the ~17% annual discount; discounting THAT again double-discounts
 * and shows e.g. Starter at 9 € (= 60% off 22,50) instead of the intended
 * 10,80 € (= 60% off 27). Basing the promo on the monthly headline matches the
 * actual checkout charge (the coupon applies to the full 12× monthly base) and
 * keeps the struck price, the discounted price and the −% pill mutually
 * consistent on Annual as well as Monthly.
 *
 * This selector fences the WIRING in PricingV2Section: the caller passes the
 * monthly headline AND the cadence-adjusted display price, and this function
 * must return the monthly headline regardless of cadence. A regression that
 * re-points the promo base back at the annual-monthly-equivalent (the #4965
 * bug) fails the unit test on this selector — the pure
 * {@link computePromoMonthlyPrice} helper can't catch it because it only sees
 * the base it's handed.
 *
 * @param monthlyHeadlinePrice the VAT-adjusted MONTHLY tier price (the headline)
 * @param _cadenceAdjustedDisplayPrice the price shown for the active cadence
 *   (on Annual this is the annual-monthly-equivalent — accepted only to make
 *   "ignore me" explicit at the call site; intentionally unused)
 */
export function selectPromoBase(
  monthlyHeadlinePrice: number,
  _cadenceAdjustedDisplayPrice: number
): number {
  // The promo base is the monthly headline for both cadences (#4965/#5011).
  // The cadence-adjusted display price is deliberately NOT used here.
  return monthlyHeadlinePrice;
}
